import React from 'react';
import { Check, X } from 'lucide-react';

interface PasswordStrengthProps {
  password: string;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthProps> = ({ password }) => {
  const getPasswordStrength = (password: string) => {
    let score = 0;
    const checks = {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /\d/.test(password),
      special: /[!@#$%^&*]/.test(password),
    };

    score = Object.values(checks).filter(Boolean).length;

    let strength = 'Very Weak';
    let color = 'var(--arc-danger)';
    let progress = 20;

    if (score >= 2) { strength = 'Weak'; color = 'var(--arc-warning)'; progress = 40; }
    if (score >= 3) { strength = 'Fair'; color = 'var(--arc-warning)'; progress = 60; }
    if (score >= 4) { strength = 'Good'; color = 'var(--arc-accent)'; progress = 80; }
    if (score === 5) { strength = 'Strong'; color = 'var(--arc-success)'; progress = 100; }

    return { strength, color, progress, checks };
  };

  if (!password) return null;

  const { strength, color, progress, checks } = getPasswordStrength(password);

  return (
    <div style={{ marginTop: 8, marginBottom: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span className="pk-small">Password strength:</span>
        <span className="pk-small" style={{ color, fontWeight: 700 }}>{strength}</span>
      </div>

      <div style={{ height: 4, borderRadius: 999, background: 'var(--arc-line-2)', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 999, width: `${progress}%`, background: color, transition: 'width var(--arc-t) var(--arc-ease)' }} />
      </div>

      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {Object.entries(checks).map(([key, valid]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {valid ? <Check size={13} className="pk-tick" /> : <X size={13} className="pk-cross" />}
            <span className="pk-small" style={{ color: valid ? 'var(--arc-success)' : 'var(--arc-ink-3)' }}>
              {key === 'length' && 'At least 8 characters'}
              {key === 'uppercase' && 'Uppercase letter'}
              {key === 'lowercase' && 'Lowercase letter'}
              {key === 'number' && 'Number'}
              {key === 'special' && 'Special character'}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PasswordStrengthIndicator;
